import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  Calendar,
  Clock,
  MapPin,
  ArrowLeft,
  Key,
  CalendarClock,
  XCircle,
  CheckCircle2,
  Circle,
  Loader2,
  Receipt,
  LogIn,
  Tag,
  BadgeCheck,
  HelpCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import { RescheduleDialog } from "@/components/booking/RescheduleDialog";
import { useAppMode } from "@/hooks/useAppMode";
import { useBooking } from "@/hooks/useBooking";
import { useProviderById } from "@/hooks/useProviders";
import { useAccessCodes, useCreateAccessCode } from "@/hooks/useAccessCodes";
import { useDevices } from "@/hooks/useDevices";
import { useCheckOut } from "@/hooks/useCheckOut";
import { useBookingCancellation } from "@/hooks/useBookingCancellation";
import { useBookingReschedule } from "@/hooks/useBookingReschedule";
import { useBookingPaymentDetails } from "@/hooks/useBookingPaymentDetails";
import { useBookingTimeCalculations } from "@/hooks/useBookingTimeCalculations";
import { useBookingCheckInLogic } from "@/hooks/useBookingCheckInLogic";

const formatTime = (t: string) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${hour12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const formatCountdown = (ms: number) => {
  if (ms <= 0) return "now";
  const totalMin = Math.ceil(ms / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const AppBookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { provider } = useAppMode();
  const brandColor = provider?.brand_primary_color || undefined;

  const [cancelOpen, setCancelOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const { data: booking, isLoading } = useBooking(bookingId);
  const { data: providerFull } = useProviderById(booking?.provider_id || "");
  const { data: accessData } = useAccessCodes(bookingId);
  const { data: devices } = useDevices(booking?.provider_id);
  const { data: paymentDetails } = useBookingPaymentDetails(booking?.id);
  const createAccessCode = useCreateAccessCode();
  const checkOut = useCheckOut();
  const cancelBooking = useBookingCancellation();
  const rescheduleBooking = useBookingReschedule();

  const accessCodes = accessData?.accessCodes || [];
  const timeSlotCodes = accessData?.timeSlotCodes || [];
  const deviceInfo = accessData?.devices || [];
  const currentAccessCode = accessCodes[0];
  const hasEnabledDevices = deviceInfo.some((d) => d.is_enabled && d.seam_device_id);

  const checkInWindowMinutes = providerFull?.check_in_window_minutes || 5;

  const status = booking?.status?.toLowerCase() || "";
  const isCompleted = status === "completed";
  const isCancelled = status === "cancelled";
  const isCheckedIn = !!booking?.checked_in_at && !booking?.checked_out_at;

  const timeCalc = useBookingTimeCalculations({
    booking,
    currentTime,
    checkInWindowMinutes,
    isCompleted,
    isCancelled,
    isCheckedIn,
  });

  const canCheckIn =
    hasEnabledDevices &&
    timeCalc.checkInAvailableTime &&
    timeCalc.sessionEnd &&
    currentTime >= timeCalc.checkInAvailableTime &&
    currentTime < timeCalc.sessionEnd &&
    !isCheckedIn &&
    !isCompleted &&
    !isCancelled;

  const canCheckOut = isCheckedIn && !isCompleted;
  const canReschedule = !!timeCalc.canReschedule;
  const canCancel = !!timeCalc.canCancel;

  const { handleCheckIn } = useBookingCheckInLogic({
    booking,
    devices: devices || [],
    currentAccessCode,
    timeSlotCodes,
    createAccessCode,
    hasEnabledDevices,
    canCheckIn,
    provider: providerFull,
  });

  // Session progress (only when checked in / during session)
  const sessionProgress = useMemo(() => {
    if (!booking || !timeCalc.bookingDateTime || !timeCalc.sessionEnd) return null;
    const start = timeCalc.bookingDateTime.getTime();
    const end = timeCalc.sessionEnd.getTime();
    const now = currentTime.getTime();
    if (now < start) return null;
    if (now >= end) return { pct: 100, remainingMs: 0, totalMs: end - start };
    return {
      pct: Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100)),
      remainingMs: end - now,
      totalMs: end - start,
    };
  }, [booking, timeCalc.bookingDateTime, timeCalc.sessionEnd, currentTime]);

  const checkInOpensInMs = useMemo(() => {
    if (!timeCalc.checkInAvailableTime) return null;
    return timeCalc.checkInAvailableTime.getTime() - currentTime.getTime();
  }, [timeCalc.checkInAvailableTime, currentTime]);

  if (isLoading) {
    return (
      <MobileAppLayout title="Booking">
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </MobileAppLayout>
    );
  }

  if (!booking) {
    return (
      <MobileAppLayout title="Booking">
        <div className="px-4 py-12 text-center">
          <p className="text-muted-foreground mb-4">Booking not found.</p>
          <Button onClick={() => navigate("/app/bookings")}>Back to bookings</Button>
        </div>
      </MobileAppLayout>
    );
  }

  const statusBadge = isCancelled ? (
    <Badge variant="destructive">Cancelled</Badge>
  ) : isCompleted ? (
    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Completed</Badge>
  ) : isCheckedIn ? (
    <Badge className="bg-blue-100 text-blue-700 border-blue-200">In Session</Badge>
  ) : (
    <Badge className="bg-green-100 text-green-700 border-green-200">Confirmed</Badge>
  );

  const handleConfirmReschedule = async (
    newDate: string,
    newStartTime: string,
    newEndTime: string,
    timeSlotId?: string
  ) => {
    await rescheduleBooking.mutateAsync({
      bookingId: booking.id,
      newDate,
      newStartTime,
      newEndTime,
      timeSlotId,
    });
    setRescheduleOpen(false);
  };

  const totalCouponDiscount =
    paymentDetails?.booking_coupons?.reduce(
      (sum, c) => sum + Number(c.discount_amount),
      0
    ) || 0;
  const originalPrice = Number(booking.price) + totalCouponDiscount;

  return (
    <MobileAppLayout title="Booking">
      <div className={`px-4 py-4 space-y-4 ${(canCheckIn || canCheckOut) ? "pb-32" : "pb-6"}`}>
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2"
          onClick={() => navigate("/app/bookings")}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>

        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {statusBadge}
            <span className="text-xs text-muted-foreground">
              #{booking.booking_number}
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight break-words">
            {booking.service_name}
          </h1>
        </div>

        {/* Session progress (checklist style, like desktop) */}
        {!isCancelled && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-medium">Session progress</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isCheckedIn || isCompleted ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span
                      className={
                        isCheckedIn || isCompleted
                          ? "text-sm font-medium text-emerald-700"
                          : "text-sm text-muted-foreground"
                      }
                    >
                      Check-in
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {timeCalc.actualCheckInTime || "Pending"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isCompleted ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span
                      className={
                        isCompleted
                          ? "text-sm font-medium text-emerald-700"
                          : "text-sm text-muted-foreground"
                      }
                    >
                      Check-out
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {timeCalc.actualCheckOutTime || "Pending"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* When & where */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                {format(new Date(booking.booking_date), "EEEE, MMMM d, yyyy")}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                {formatTime(booking.start_time)} – {formatTime(booking.end_time)}
              </span>
            </div>
            {booking.address && (
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{booking.address}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Access code / access details */}
        {!isCancelled && !isCompleted && (
          <Card id="access-details-section">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Entrance access</span>
              </div>

              {currentAccessCode ? (
                <div className="space-y-3">
                  <div
                    className="rounded-lg p-4 text-center"
                    style={{
                      backgroundColor: brandColor
                        ? `${brandColor}15`
                        : "hsl(var(--muted))",
                    }}
                  >
                    <p className="text-xs text-muted-foreground mb-1">Your code</p>
                    <p
                      className="text-3xl font-mono font-bold tracking-widest"
                      style={{ color: brandColor }}
                    >
                      {currentAccessCode.code}
                    </p>
                  </div>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="w-full">
                        <HelpCircle className="h-4 w-4 mr-2" />
                        How to use the code
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle className="flex items-center">
                          <Key className="h-5 w-5 mr-2" />
                          How to Use Your Entrance Code
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 py-2">
                        {[
                          "At the start of your session time, touch the keypad on the door for the digits to light up.",
                          "Enter the 4 digit code and press the unlock icon.",
                          "The door will unlock and you can enter the room.",
                        ].map((step, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 bg-muted text-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                              {i + 1}
                            </span>
                            <p className="text-sm text-muted-foreground">{step}</p>
                          </div>
                        ))}
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-destructive/10 text-destructive rounded-full flex items-center justify-center text-sm font-semibold">
                            4
                          </span>
                          <p className="text-sm text-muted-foreground">
                            At the end of the session, return to your booking page and tap check out.{" "}
                            <span className="font-semibold text-destructive">
                              Failing to check out can result in overstay charges.
                            </span>
                          </p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              ) : hasEnabledDevices ? (
                canCheckIn ? (
                  <p className="text-sm text-muted-foreground">
                    Tap "Check in" below to generate your entrance code.
                  </p>
                ) : checkInOpensInMs !== null && checkInOpensInMs > 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Check-in opens in{" "}
                    <span className="font-medium text-foreground">
                      {formatCountdown(checkInOpensInMs)}
                    </span>{" "}
                    ({checkInWindowMinutes} min before start).
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Check-in is not currently available.
                  </p>
                )
              ) : (
                <p className="text-sm text-muted-foreground">
                  This location uses manual entry. Please contact the provider for
                  instructions.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Payment</span>
            </div>

            {totalCouponDiscount > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Original</span>
                <span className="text-muted-foreground line-through">
                  £{originalPrice.toFixed(2)}
                </span>
              </div>
            )}

            {paymentDetails?.booking_coupons?.map((coupon) => (
              <div
                key={coupon.id}
                className="flex items-center justify-between text-sm rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2"
              >
                <span className="flex items-center gap-1.5 text-emerald-800">
                  <Tag className="h-3.5 w-3.5" />
                  {coupon.user_coupon.coupon.name}
                </span>
                <span className="font-medium text-emerald-800">
                  -£{Number(coupon.discount_amount).toFixed(2)}
                </span>
              </div>
            ))}

            {paymentDetails?.membership_info && (
              <div className="flex items-start justify-between gap-2 text-sm rounded-md bg-blue-50 border border-blue-200 px-3 py-2">
                <span className="flex items-start gap-1.5 text-blue-800 min-w-0 flex-1">
                  <BadgeCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span className="break-words">
                    {paymentDetails.membership_info.membership?.name ||
                      "Membership session"}
                  </span>
                </span>
                <span className="font-medium text-blue-800 whitespace-nowrap flex-shrink-0">
                  Session used
                </span>
              </div>
            )}

            <Separator />

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="font-semibold text-lg">
                £{Number(booking.price).toFixed(2)}
              </span>
            </div>

            {Number(booking.price) === 0 ? (
              <p className="text-xs text-emerald-600">
                ✓ No payment required
              </p>
            ) : (
              <p className="text-xs text-emerald-600">✓ Payment confirmed</p>
            )}
          </CardContent>
        </Card>

        {/* Secondary actions */}
        {(canReschedule || canCancel) && (
          <div className="space-y-2 pt-2">
            {canReschedule && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setRescheduleOpen(true)}
              >
                <CalendarClock className="h-4 w-4 mr-2" />
                Reschedule
              </Button>
            )}
            {canCancel && (
              <Button
                variant="ghost"
                className="w-full text-destructive hover:text-destructive"
                onClick={() => setCancelOpen(true)}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancel booking
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Floating check-in / check-out button */}
      {(canCheckIn || canCheckOut) && (
        <div
          className="fixed left-0 right-0 z-40 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+12px)] bg-gradient-to-t from-background via-background to-background/80 backdrop-blur border-t"
          style={{ bottom: "var(--app-bottom-tab-offset, 64px)" }}
        >
          <div className="mx-auto max-w-md">
            {canCheckIn ? (
              <Button
                className="w-full shadow-lg h-14 text-base font-semibold bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                size="lg"
                onClick={handleCheckIn}
                disabled={createAccessCode.isPending}
              >
                <CheckCircle2 className="h-6 w-6 mr-2" />
                {createAccessCode.isPending ? "Generating Code..." : "Check In Now"}
              </Button>
            ) : (
              <Button
                className="w-full shadow-lg h-14 text-base font-semibold bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
                size="lg"
                onClick={() => checkOut.mutate(booking.id)}
                disabled={checkOut.isPending}
              >
                <CheckCircle2 className="h-6 w-6 mr-2" />
                {checkOut.isPending ? "Checking Out..." : "Check Out"}
              </Button>
            )}
          </div>
        </div>
      )}

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Refund eligibility depends on the provider's policy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cancelBooking.mutate(booking.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RescheduleDialog
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        booking={booking}
        onReschedule={handleConfirmReschedule}
        isLoading={rescheduleBooking.isPending}
      />
    </MobileAppLayout>
  );
};

export default AppBookingDetails;
